import {
  CalculationType,
  Spotlight,
  SpotlightTypes,
  spotlightsMap
} from 'components/SuburbSpotlight';
import { Location, LocationType, TotalProductsType } from 'types';

// Get location with the highest number of settled for a given key and locationType
function getLocationWithHighestNumSettled(
  data: Location[],
  key: keyof Location,
  locationType: LocationType,
  calculationType?: CalculationType
): Location | undefined {
  if (!data) return undefined;
  // Filter data by locationType
  const filteredData = data.filter(
    (location) => location.locationType === locationType
  );
  return filteredData.reduce((prev: Location | undefined, current) => {
    const prevValue = prev ? prev[key] ?? 0 : 0;
    const currentValue = current[key] ?? 0;

    if (calculationType === 'percentageSettled') {
      const prevNumSettled = prev ? prev.numSettled ?? 0 : 0;
      const currentNumSettled = current.numSettled ?? 0;
      // Check if currentNumSettled and prevNumSettled are not 0
      if (currentNumSettled !== 0 && prevNumSettled !== 0) {
        return Number(currentValue) / currentNumSettled >
          Number(prevValue) / prevNumSettled
          ? current
          : prev;
      }
    }

    return currentValue > prevValue ? current : prev;
  }, undefined);
}

export function getSuburbSpotlights(data: Location[]) {
  const spotlights: Spotlight[] = [];

  for (const spotlightType in spotlightsMap) {
    const { field, calculationType } =
      spotlightsMap[spotlightType as SpotlightTypes];

    if (field) {
      const location = getLocationWithHighestNumSettled(
        data,
        field,
        'Suburb',
        calculationType
      );

      if (location && location[field]) {
        spotlights.push({
          spotlightType: spotlightType as SpotlightTypes,
          suburb: location.location,
          numOfSettled:
            calculationType === 'percentageSettled'
              ? Number(location[field]) / Number(location.numSettled)
              : Number(location[field]),
          location
        });
      }
    }
  }

  return spotlights;
}

export function getLocationsSuburb(
  locations: Location[]
): Location[] | undefined {
  return locations?.filter((location) => location.locationType === 'Suburb');
}

export function getLocationState(locations: Location[]): Location | undefined {
  return locations?.find((location) => location.locationType === 'State');
}

export function getTotalProducts(location: Location): TotalProductsType[] {
  const products: TotalProductsType[] = [
    {
      name: 'Solar System',
      total: location.numSettledSolarSystem ?? 0,
      key: 'numSettledSolarSystem'
    },
    {
      name: 'Battery Storage',
      total: location.numSettledBatteryStorage ?? 0,
      key: 'numSettledBatteryStorage'
    },
    {
      name: 'Solar & Battery System',
      total: location.numSettledSolarAndBatterySystem ?? 0,
      key: 'numSettledSolarAndBatterySystem'
    },
    {
      name: 'Electric Cars (New)',
      total: location.numSettledElectricCarNew ?? 0,
      key: 'numSettledElectricCarNew'
    },
    {
      name: 'Electric Cars (Used)',
      total: location.numSettledElectricCarUsed ?? 0,
      key: 'numSettledElectricCarUsed'
    },
    {
      name: 'Electric Motorbikes (New)',
      total: location.numSettledElectricMotorbikeNew ?? 0,
      key: 'numSettledElectricMotorbikeNew'
    },
    {
      name: 'Electric Motorbikes (Used)',
      total: location.numSettledElectricMotorbikeUsed ?? 0,
      key: 'numSettledElectricMotorbikeUsed'
    },
    {
      name: 'EV Charging',
      total: location.numSettledEvChargingInfrastructure ?? 0,
      key: 'numSettledEvChargingInfrastructure'
    },
    {
      name: 'Electric Stove Tops',
      total: location.numSettledEfficientElectricStoveTops ?? 0,
      key: 'numSettledEfficientElectricStoveTops'
    },
    {
      name: 'Reverse Cycle Systems',
      total:
        location.numSettledElectricHeatingAndCoolingSystemsReverseCycle ?? 0,
      key: 'numSettledElectricHeatingAndCoolingSystemsReverseCycle'
    },
    {
      name: 'Hot Water Heat Pumps',
      total: location.numSettledHotWaterHeatPumpsHwhp ?? 0,
      key: 'numSettledHotWaterHeatPumpsHwhp'
    },
    {
      name: 'Ceiling Insulation',
      total: location.numSettledInsulation ?? 0,
      key: 'numSettledInsulation'
    }
  ];

  // return products with highest total first and remove products with total of 0
  return products
    .filter((product) => product.total > 0)
    .sort((a, b) => b.total - a.total);
}

export function formatToProductName(text?: string) {
  switch (text) {
    case 'Batteries':
    case 'BatteryStorage':
      return 'Battery';

    case 'SolarSystem':
    case 'SolarPanels':
    case 'Solar':
      return 'Solar';

    case 'SolarSystemAndBatteryCombo':
    case 'SolarAndBatterySystem':
      return 'Solar & Battery System';

    case 'SolarInverters':
      return 'Solar Inverters';

    case 'SolarPoolHeatingSystems':
      return 'Solar Pool Heating Systems';

    case 'AirConditioningAndVentilation':
      return 'Air Conditioning and Ventilation';

    case 'EvChargingInfrastructure':
      return 'EV Charging Infrastructure';

    case 'Efficient Electric Stove Tops':
      return 'Efficient Electric Stove Tops';

    case 'ElectricHeatingAndCoolingSystemsReverseCycle':
      return 'Electric Heating And Cooling Systems Reverse Cycle';

    case 'HotWaterHeatPumpsHwhp':
      return 'Hot Water Heat Pumps';

    case 'ElectricVehicles':
      return 'Electric Vehicles';

    case 'ElectricCarNew':
      return 'Electric Car (New)';

    case 'ElectricCarUsed':
      return 'Electric Car (Used)';

    case 'HydronicHeatPumps':
      return 'Hydronic Heat Pumps';

    case 'Insulation':
      return 'Insulation';

    case 'ElectricMotorbikeNew':
      return 'Electric Motorbike (New)';

    case 'ElectricMotorbikeUsed':
      return 'Electric Motorbike (Used)';

    default:
      return null;
  }
}
